/**
 * Lead & Opportunity Type Definitions
 * For high-ticket consultative sales workflow
 */

import { Timestamp } from 'firebase/firestore';

export type LeadStatus = 
  | 'new'
  | 'qualifying'
  | 'demo_scheduled'
  | 'demo_completed'
  | 'proposal_sent'
  | 'negotiating'
  | 'closed_won'
  | 'closed_lost';

export type LeadSource = 
  | 'website'
  | 'linkedin'
  | 'referral'
  | 'cold_outreach'
  | 'paid_ad'
  | 'other';

export type PricingTier = 
  | '10-20'
  | '20-50'
  | '50-100'
  | '100-250'
  | '250+';

export type ActivityType =
  | 'call'
  | 'email'
  | 'demo'
  | 'proposal'
  | 'meeting'
  | 'note';

export interface ContactInfo {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  position?: string;
}

export interface BusinessInfo {
  doorCount: number;
  pricingTier?: PricingTier;
  currentSolution?: string;
  currentSystem?: string; // For AI email prompts
  painPoints: string[];
  budget?: string;
  timeline?: string;
  province?: string;
  city?: string;
  role?: string; // Contact's role in decision-making
}

export interface LeadActivity {
  id: string;
  type: ActivityType;
  subject: string;
  description?: string;
  performedBy: string;
  performedByName?: string;
  timestamp: Timestamp;
  metadata?: Record<string, any>;
}

export interface EmailHistory {
  id: string;
  subject: string;
  body: string;
  sentAt: Timestamp;
  sentBy: string;
  sentByName?: string;
  generatedByAI: boolean;
  status: 'sent' | 'failed' | 'bounced';
  messageId?: string;
}

export interface Lead {
  id: string;
  status: LeadStatus;
  source: LeadSource;
  
  contactInfo: ContactInfo;
  businessInfo: BusinessInfo;
  
  assignedTo?: string;
  assignedToName?: string;
  
  score: number;                    // 0-100 lead score
  estimatedValue: number;           // Expected annual contract value
  probability: number;              // % chance to close (0-100)
  
  demoScheduledFor?: Timestamp;
  demoCompletedAt?: Timestamp;
  proposalSentAt?: Timestamp;
  closedAt?: Timestamp;
  
  lostReason?: string;
  wonDetails?: {
    contractId?: string;
    finalValue: number;
  };
  
  activities: LeadActivity[];
  
  // Email automation fields
  emailHistory?: EmailHistory[];
  needsFollowup?: boolean;
  autoFollowupEnabled?: boolean;
  nextFollowupDate?: Timestamp;
  lastEmailSentAt?: Timestamp;
  
  notes?: string;
  pipelineNotes?: string; // For AI email context
  tags?: string[];
  
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy?: string;
}

export interface LeadCreateData {
  source: LeadSource;
  contactInfo: ContactInfo;
  businessInfo: Partial<BusinessInfo>;
  notes?: string;
  tags?: string[];
}

export interface LeadUpdateData {
  status?: LeadStatus;
  contactInfo?: Partial<ContactInfo>;
  businessInfo?: Partial<BusinessInfo>;
  assignedTo?: string;
  assignedToName?: string;
  score?: number;
  estimatedValue?: number;
  probability?: number;
  demoScheduledFor?: Timestamp;
  demoCompletedAt?: Timestamp;
  proposalSentAt?: Timestamp;
  lostReason?: string;
  notes?: string;
  tags?: string[];
}

export interface LeadFilters {
  status?: LeadStatus;
  source?: LeadSource;
  assignedTo?: string;
  minDoorCount?: number;
  maxDoorCount?: number;
  pricingTier?: PricingTier;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'score' | 'estimatedValue' | 'doorCount';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface LeadStats {
  total: number;
  byStatus: Record<LeadStatus, number>;
  bySource: Record<LeadSource, number>;
  avgDoorCount: number;
  avgScore: number;
  totalPipelineValue: number;
  weightedPipelineValue: number;
  conversionRates: {
    leadToDemo: number;
    demoToProposal: number;
    proposalToClose: number;
    overallCloseRate: number;
  };
}

/**
 * Calculate pricing tier based on door count
 */
export function calculatePricingTier(doorCount: number): PricingTier {
  if (doorCount < 20) return '10-20';
  if (doorCount < 50) return '20-50';
  if (doorCount < 100) return '50-100';
  if (doorCount < 250) return '100-250';
  return '250+';
}

/**
 * Calculate estimated annual value based on pricing tier
 */
export function calculateEstimatedValue(doorCount: number): number {
  const tier = calculatePricingTier(doorCount);
  
  const tierPricing: Record<PricingTier, { min: number; max: number }> = {
    '10-20': { min: 999, max: 1499 },
    '20-50': { min: 1499, max: 2999 },
    '50-100': { min: 2999, max: 4999 },
    '100-250': { min: 4999, max: 9999 },
    '250+': { min: 9999, max: 15000 },
  };
  
  const pricing = tierPricing[tier];
  return Math.round((pricing.min + pricing.max) / 2);
}

/**
 * Calculate lead score based on various factors
 */
export function calculateLeadScore(lead: Partial<Lead>): number {
  let score = 0;
  
  // Door count (40 points max)
  if (lead.businessInfo?.doorCount) {
    const doorCount = lead.businessInfo.doorCount;
    if (doorCount >= 100) score += 40;
    else if (doorCount >= 50) score += 35;
    else if (doorCount >= 20) score += 30;
    else if (doorCount >= 10) score += 20;
    else score += 10;
  }
  
  // Source quality (20 points max)
  const sourceScores: Record<LeadSource, number> = {
    referral: 20,
    website: 15,
    linkedin: 12,
    cold_outreach: 8,
    paid_ad: 10,
    other: 5,
  };
  if (lead.source) {
    score += sourceScores[lead.source];
  }
  
  // Pain points identified (20 points max)
  if (lead.businessInfo?.painPoints) {
    const painPointCount = lead.businessInfo.painPoints.length;
    score += Math.min(painPointCount * 5, 20);
  }
  
  // Budget indication (10 points)
  if (lead.businessInfo?.budget) {
    score += 10;
  }
  
  // Timeline urgency (10 points)
  if (lead.businessInfo?.timeline) {
    const timeline = lead.businessInfo.timeline.toLowerCase();
    if (timeline.includes('urgent') || timeline.includes('asap')) score += 10;
    else if (timeline.includes('week') || timeline.includes('month')) score += 7;
    else score += 3;
  }
  
  return Math.min(score, 100);
}

/**
 * Get lead status display name
 */
export function getLeadStatusDisplay(status: LeadStatus): string {
  const displays: Record<LeadStatus, string> = {
    new: 'New Lead',
    qualifying: 'Qualifying',
    demo_scheduled: 'Demo Scheduled',
    demo_completed: 'Demo Completed',
    proposal_sent: 'Proposal Sent',
    negotiating: 'Negotiating',
    closed_won: 'Closed Won',
    closed_lost: 'Closed Lost',
  };
  return displays[status];
}

/**
 * Get lead status color for UI
 */
export function getLeadStatusColor(status: LeadStatus): string {
  const colors: Record<LeadStatus, string> = {
    new: 'blue',
    qualifying: 'purple',
    demo_scheduled: 'cyan',
    demo_completed: 'teal',
    proposal_sent: 'orange',
    negotiating: 'yellow',
    closed_won: 'green',
    closed_lost: 'red',
  };
  return colors[status];
}
